import { sendLegacyChartTheme } from '../services/legacyChartThemeBridge';
import { cleanChartDiagnostic, chartTimeoutSnapshot, validChartOutcome } from '../services/legacyChartDiagnostics';
import { resolveLegacyChartUrl } from '../adapters/legacyChartUrl';
import { legacyChartPrefetchAll, legacyChartPrefetchPlan, legacyChartScrollCooldown } from '../services/legacyChartPrefetch';

let navigation = 0;
export const chartLoadTimeout = 30000;

export function isOwnedChart(url) {
  return Boolean(url) && new URL(url, window.location.href).pathname === '/stockchart';
}
function reusable(url) {
  return isOwnedChart(url) && !new URL(url, window.location.href).searchParams.has('id');
}

export default {
  data: () => ({ iframeLoadId: '', iframeSessionId: '', iframeBaseUrl: '', iframeReusable: false, iframeError: '', iframeCacheHit: false, iframePreparing: false, iframeRetained: false, chartLastTiming: null, iframePartial: false, iframeStalled: false }),
  computed: {
    chartFrameUrl() {
      const base = this.iframeBaseUrl || this.url_stock;
      if (!isOwnedChart(base)) return base;
      const url = new URL(base, window.location.href);
      url.searchParams.set('chart_load_id', this.iframeSessionId || this.iframeLoadId);
      return url.href;
    },
    legacyChartScope() {
      return JSON.stringify([this._g_User && this._g_User.uid, this._g_User && this._g_User.roles,
        this._g_User && this._g_User.permissions, this._g_UserFeatures,
        this.$store ? this.$store.getters._g_portfolio_id_selected : this._g_portfolio_id_selected]);
    }
  },
  watch: {
    _g_option_mobile(option) {
      if (!this.isMobile) return;
      if (option) { this.iframeReusable = false; clearTimeout(this.chartLoadTimer); }
      else if (this.url_stock) this.startChartLoad();
    },
    isMobile() { if (this.url_stock) this.startChartLoad(); },
    legacyChartScope() { if (this.url_stock) this.startChartLoad(); },
    _g_stocks() { this.$nextTick(() => this.prefetchChartNeighbours()); }
  },
  created() {
    window.addEventListener('message', this.handleChartReady);
    window.addEventListener('focus', this.handleChartWindowFocus);
    window.addEventListener('blur', this.handleChartWindowBlur);
    window.addEventListener('storage', this.handleChartStorage);
    window.addEventListener('legacy-chart-context-changed', this.reloadChartContext);
    document.addEventListener('visibilitychange', this.validateChartContext);
  },
  beforeDestroy() {
    window.removeEventListener('message', this.handleChartReady);
    window.removeEventListener('focus', this.handleChartWindowFocus);
    window.removeEventListener('blur', this.handleChartWindowBlur);
    window.removeEventListener('storage', this.handleChartStorage);
    window.removeEventListener('legacy-chart-context-changed', this.reloadChartContext);
    document.removeEventListener('visibilitychange', this.validateChartContext);
    clearTimeout(this.chartLoadTimer);
    clearTimeout(this.chartPrefetchTimer);
    clearTimeout(this.chartFocusTimer);
  },
  methods: {
    handleChartWindowBlur() {
      clearTimeout(this.chartFocusTimer);
      this.chartFocusTimer = setTimeout(() => {
        // Moving between the native iframe and the list also blurs window.
        // The document keeps focus in that case; only leaving the view expires it.
        this.chartWindowWasUnfocused = !document.hasFocus();
        if (this.chartWindowWasUnfocused && this.iframeReusable) this.chartMessage('legacy-chart-suspend');
      }, 0);
    },
    handleChartWindowFocus() {
      if (!this.chartWindowWasUnfocused) return;
      this.chartWindowWasUnfocused = false;
      this.validateChartContext();
    },
    validateChartContext() {
      if (this.iframeReusable) this.chartMessage(document.hidden ? 'legacy-chart-suspend' : 'legacy-chart-validate');
    },
    reloadChartContext() { if (this.url_stock) this.startChartLoad(); },
    handleChartStorage(event) {
      if (event.key === null || ['uid', 'user_access_token'].includes(event.key)) this.reloadChartContext();
    },
    _url_stock() {
      const selected = this._g_SelectedTicker;
      if (!selected || !selected.chart) return;
      // Override the shared legacy concatenation only in this chart feature.
      const value = selected.chart.url === undefined ? 'aapl' : selected.chart.url;
      this.url_stock = resolveLegacyChartUrl(value, process.env.VUE_APP_API || window.location.href);
    },
    chartFrame() { return this.$el.querySelector(this.isMobile ? '#mobile-iframe' : '#iframe'); },
    chartMessage(type, extra = {}) {
      const frame = this.chartFrame();
      if (!frame || !isOwnedChart(frame.src)) return;
      const origin = new URL(frame.src, window.location.href).origin;
      frame.contentWindow.postMessage({ type, sessionId: this.iframeSessionId, loadId: this.iframeLoadId, ...extra }, origin);
    },
    startChartLoad(url) {
      // A selection is an explicit intent to explore this zone, even if the
      // scroll cooldown has not expired. Readiness will start its warm plan.
      this.cancelChartPrefetchCooldown();
      const force = !url;
      url = url || this.url_stock;
      clearTimeout(this.chartLoadTimer);
      this.isIframeLoading = true;
      this.iframeError = '';
      this.iframePartial = false; this.iframeStalled = false;
      this.chartLoadStarted = Date.now();
      this.chartDiagnosticSeen = new Set(); this.chartLastDiagnostic = {};
      this.chartLoadTicker = this._g_SelectedTicker && this._g_SelectedTicker.ticker || '';
      this.iframeCacheHit = false;
      this.iframePreparing = false;
      this.iframeRetained = false;
      this.iframeLoadId = Date.now() + '-' + (++navigation);
      this.trackChartDiagnostic('started', { load_id: this.iframeLoadId, stage: 'initializing', elapsed_ms: 0 });
      const existingFrame = this.chartFrame();
      const sameOrigin = this.iframeBaseUrl && new URL(url, window.location.href).origin === new URL(this.iframeBaseUrl, window.location.href).origin;
      if (!force && this.iframeReusable && existingFrame && sameOrigin && reusable(url) && !(this._g_User && this._g_User.is_pre_eureker_user)) {
        this.iframePreparing = true;
        // Use the actual frame origin (also supports isolated preview proxies).
        const target = new URL(url, window.location.href), frame = new URL(existingFrame.src, window.location.href);
        target.host = frame.host; target.protocol = frame.protocol;
        this.chartMessage('legacy-chart-select', { url: target.href });
      } else {
        this.iframeReusable = false;
        this.iframeSessionId = this.iframeLoadId;
        this.iframeBaseUrl = url;
      }
      if (isOwnedChart(url) && !(this.isMobile && this._g_option_mobile)) {
        this.chartLoadTimer = setTimeout(() => {
          this.trackChartDiagnostic('timeout', { ...this.chartLastDiagnostic, ...chartTimeoutSnapshot(this.chartFrame(), this.iframeLoadId, this.chartLoadStarted), bridge_attached: this.chartDiagnosticSeen.has('attached') });
          this.iframeReusable = false;
          this.iframePreparing = false;
          this.iframeRetained = false;
          this.iframeError = 'No se ha podido cargar el gráfico. Vuelve a intentarlo.';
        }, chartLoadTimeout);
      }
    },
    prefetchChartNeighbours() {
      if (!this.iframeReusable || this._g_User && this._g_User.is_pre_eureker_user) return;
      const initial = this.chartPrefetchSession !== this.iframeSessionId;
      const list = this.$refs && this.$refs.legacyStocks;
      const entries = (list ? (list.not_ordered ? list.stocks : list.ordered_stocks) : this._g_stocks) || [];
      if (initial || legacyChartPrefetchAll(entries)) this.cancelChartPrefetchCooldown();
      if (this.chartScrollUntil > Date.now()) return;
      const frame = this.chartFrame();
      if (!frame) return;
      const base = new URL(frame.src, window.location.href);
      const viewport = list && list.$el && list.$el.querySelector('#my-list');
      const plan = legacyChartPrefetchPlan(entries, this._g_SelectedTicker, viewport);
      const urls = plan.entries
        .filter(entry => entry && entry.nom !== '---' && entry.chart && entry.chart.url)
        .map(entry => {
          const url = resolveLegacyChartUrl(entry.chart.url, base.href);
          return url && reusable(url) ? url : null;
        }).filter(Boolean);
      const order = entries.map(entry => String(entry && entry.ticker || '').toUpperCase());
      const orderKey = order.join('\0');
      const changedOrder = initial || orderKey !== this.chartCacheOrderKey;
      this.chartPrefetchSession = this.iframeSessionId;
      this.chartCacheOrderKey = orderKey;
      this.chartMessage('legacy-chart-prefetch', { urls, anchor: plan.anchor, ...(changedOrder ? { order } : {}) });
    },
    cancelChartPrefetchCooldown() {
      clearTimeout(this.chartPrefetchTimer);
      this.chartScrollUntil = 0;
    },
    scheduleChartPrefetch() {
      const list = this.$refs && this.$refs.legacyStocks;
      const entries = list ? (list.not_ordered ? list.stocks : list.ordered_stocks) : this._g_stocks;
      if (legacyChartPrefetchAll(entries)) {
        this.cancelChartPrefetchCooldown();
        this.prefetchChartNeighbours();
        return;
      }
      clearTimeout(this.chartPrefetchTimer);
      this.chartScrollUntil = Date.now() + legacyChartScrollCooldown;
      // Stop the pending plan while scrolling; an already running bounded
      // batch may finish and remain useful, but no further batch is started.
      this.chartMessage('legacy-chart-prefetch', { urls: [] });
      this.chartPrefetchTimer = setTimeout(() => {
        this.chartScrollUntil = 0;
        this.prefetchChartNeighbours();
      }, legacyChartScrollCooldown);
    },
    trackChartDiagnostic(outcome, value) {
      try {
        if (!validChartOutcome(outcome) || this.chartDiagnosticSeen && this.chartDiagnosticSeen.has(outcome)) return;
        const diagnostic = cleanChartDiagnostic(value);
        this.chartLastDiagnostic = diagnostic;
        if (this.chartDiagnosticSeen) this.chartDiagnosticSeen.add(outcome);
        if (this.chartUsage) this.chartUsage.track('diagnostic', 'chart_load', outcome, { ticker: this.chartLoadTicker || '', diagnostic });
      } catch (_) { /* Diagnostics never control rendering. */ }
    },
    handleChartReady(event) {
      const data = event.data;
      if (!data || !['legacy-chart-ready', 'legacy-chart-reload', 'legacy-chart-loading', 'legacy-chart-diagnostic'].includes(data.type) || data.loadId !== this.iframeLoadId) return;
      const frame = this.chartFrame();
      if (!frame || !isOwnedChart(frame.src) || event.source !== frame.contentWindow) return;
      const url = new URL(frame.src, window.location.href);
      if (event.origin !== url.origin || url.searchParams.get('chart_load_id') !== this.iframeSessionId ||
        (data.sessionId && data.sessionId !== this.iframeSessionId)) return;
      if (data.type === 'legacy-chart-diagnostic') {
        this.trackChartDiagnostic(data.outcome, data.diagnostic);
        return;
      }
      if (data.type === 'legacy-chart-loading') {
        if (this.isIframeLoading) {
          this.iframePreparing = false;
          this.iframeCacheHit = data.cached === true;
          this.iframeRetained = this.iframeCacheHit && data.retained === true;
        }
        return;
      }
      if (data.type === 'legacy-chart-reload') { this.startChartLoad(); return; }
      clearTimeout(this.chartLoadTimer);
      if (data.error) {
        this.iframePartial = false; this.iframeStalled = false;
        this.isIframeLoading = true;
        this.iframePreparing = false;
        this.iframeRetained = false;
        this.iframeReusable = false;
        this.iframeError = 'No se ha podido cargar el gráfico. Vuelve a intentarlo.';
        return;
      }
      const wasReady = !this.isIframeLoading;
      this.iframePartial = data.partial === true; this.iframeStalled = data.stalled === true;
      this.iframeReusable = Boolean(data.reusable);
      this.chartLastTiming = data.timings || null;
      this.iframeError = '';
      this.isIframeLoading = false;
      if (!wasReady) { this._checkAllLoaded(); this.prefetchChartNeighbours(); }
    },
    handleIframeLoad(event) {
      const frame = event && (event.target || event.currentTarget);
      if (!frame || !frame.src) return;
      sendLegacyChartTheme(frame, this.legacyDarkTheme);
      if (isOwnedChart(frame.src)) return;
      clearTimeout(this.chartLoadTimer);
      this.isIframeLoading = false;
      this._checkAllLoaded();
    }
  }
};
